import React from 'react';
import { Virtuoso } from 'react-virtuoso';
import { styled } from '@components-shared/src/stitches';
import { Spinner } from '@components/basics/Spinner';
import { StyledScroll } from '@components/main-components/containers/RadixScroll';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment, UserModalInfoFragmentDoc } from '@graphql/reactQuery';
import { DataTestId } from '@utils/test';
import { User } from './User';

export const UserListModalBody = ({
  userList,
  loading,
  loadMore,
}: {
  userList: FragmentType<UserModalInfoFragmentDoc>[] | undefined;
  loading: boolean;
  loadMore: () => void;
}) => {
  const [scrollParent, setScrollParent] = React.useState<HTMLDivElement | undefined>();

  const itemContent = React.useCallback(
    (
      _: number,
      collectorFragment: FragmentType<UserModalInfoFragmentDoc>,
      // gotcha: first param is the index assigned by virtuoso, second is the actual item data
    ) => {
      const collector = getFragment(UserModalInfoFragmentDoc, collectorFragment);
      return (
        <div key={collector.id} data-testid={DataTestId.COLLECTED_BY_MODAL_ROW}>
          <User user={collectorFragment} />
        </div>
      );
    },
    [],
  );

  const Footer = React.useCallback(() => {
    if (userList && userList.length <= 4) return null;
    return <StyledFooter />;
  }, [userList]);

  return (
    <>
      {loading ? (
        <SpinnerContainer>
          <Spinner size="xl" />
        </SpinnerContainer>
      ) : (
        <ScrollArea
          ref={r => {
            setScrollParent(r || undefined);
          }}
          css={{
            height: '100%',
            minHeight: 300,
            maxHeight: 400,

            '@tablet': {
              maxHeight: 500,
            },
          }}
        >
          <Virtuoso
            data={userList}
            itemContent={itemContent}
            customScrollParent={scrollParent}
            endReached={loadMore}
            components={{ List, Footer }}
          />
        </ScrollArea>
      )}
    </>
  );
};

const ScrollArea = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  overflow: 'auto',

  ...StyledScroll,
});
const SpinnerContainer = styled('div', {
  height: 500,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const List = styled('div', {
  display: 'flex',
  flexDirection: 'column',
});

// React Virtuoso uses paddingBottom internally so we're using Virtuoso's footer element instead
const StyledFooter = styled('div', {
  height: 28,
});
